/**
 * TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 / LAUNCH CONTROL
 * =====================================================
 * Simple Super-Admin surface to toggle the existing Soft Launch gate.
 * Reuses the canonical authority ONLY:
 *   GET /api/admin/soft-launch-gate  (full state + audit history)
 *   PUT /api/admin/soft-launch-gate  ({enabled, reason})
 * No parallel state, no launch-management module.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import api from '../../utils/api';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { ArrowLeft, Loader2, Rocket, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function LaunchControlPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { admin } = useTreatXAdminAuth();
  // TREATX-LAUNCH-CONTROL-SUPERADMIN-01 — UI exposure stays Super-Admin-only.
  // Route admission reuses the working `content.manage` gate (same as the
  // Invites tab); this in-page guard bounces any non-super-admin who reaches
  // the page directly. Server-side PUT authority remains the authoritative
  // protection.
  const isSuperAdmin = admin?.role === 'treatx_super_admin';
  useEffect(() => {
    if (admin && !isSuperAdmin) {
      navigate('/treatx-admin/dashboard', { replace: true });
    }
  }, [admin, isSuperAdmin, navigate]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(null);
  const [reason, setReason] = useState('');

  const fetchState = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/soft-launch-gate');
      setState(data || null);
    } catch (e) {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) fetchState(); }, [fetchState, isSuperAdmin]);

  const requestToggle = (next) => {
    setPendingEnabled(next);
    setReason('');
    setDialogOpen(true);
  };

  const confirmToggle = async () => {
    if (pendingEnabled === null) return;
    setSaving(true);
    try {
      const { data } = await api.put('/admin/soft-launch-gate', {
        enabled: pendingEnabled,
        reason: reason.trim() || null,
      });
      setState(data || null);
      toast.success(t('launchControl.saved'));
      setDialogOpen(false);
      setPendingEnabled(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('launchControl.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const enabled = state?.enabled === true;
  const history = Array.isArray(state?.history) ? [...state.history].reverse() : [];

  if (admin && !isSuperAdmin) return null;

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-8 py-6" data-testid="launch-control-page">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')}
                className="mb-3" data-testid="launch-control-back">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> {t('launchControl.backToDashboard')}
        </Button>

        <div className="flex items-center gap-2 mb-1">
          <Rocket className="h-5 w-5 text-blue-700" />
          <h1 className="text-xl font-semibold text-slate-900" data-testid="launch-control-title">
            {t('launchControl.title')}
          </h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">{t('launchControl.subtitle')}</p>

        {loading ? (
          <div className="text-center py-12 text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : (
          <>
            <Card className="mb-6">
              <CardContent className="py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {enabled
                      ? <ShieldAlert className="h-6 w-6 text-amber-600 mt-0.5" />
                      : <ShieldCheck className="h-6 w-6 text-green-600 mt-0.5" />}
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">
                        {t('launchControl.currentState')}
                      </div>
                      <div
                        className={`text-sm font-semibold ${enabled ? 'text-amber-700' : 'text-green-700'}`}
                        data-testid="launch-control-state"
                      >
                        {enabled ? t('launchControl.gateOn') : t('launchControl.gateOff')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{t('launchControl.toggleLabel')}</span>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(v) => requestToggle(!!v)}
                      disabled={saving}
                      data-testid="launch-control-toggle"
                    />
                  </div>
                </div>

                {state?.message && (
                  <div className="mt-4 text-[12px] text-slate-500" data-testid="launch-control-message">
                    <span className="font-medium text-slate-600">{t('launchControl.publicMessage')}: </span>
                    {state.message}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-slate-400">
                  {state?.updated_at && (
                    <span data-testid="launch-control-updated-at">
                      {t('launchControl.lastUpdated')}: {formatDateTimeLocale(state.updated_at)}
                    </span>
                  )}
                  {state?.updated_by && (
                    <span data-testid="launch-control-updated-by">
                      {t('launchControl.updatedBy')}: {state.updated_by}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="py-4 overflow-x-auto">
                <h2 className="text-sm font-semibold text-slate-900 mb-3">{t('launchControl.history')}</h2>
                {history.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center" data-testid="launch-control-history-empty">
                    {t('launchControl.historyEmpty')}
                  </p>
                ) : (
                  <table className="min-w-full text-left text-xs" data-testid="launch-control-history">
                    <thead className="text-slate-500 uppercase">
                      <tr>
                        <th className="px-3 py-2">{t('launchControl.colWhen')}</th>
                        <th className="px-3 py-2">{t('launchControl.colState')}</th>
                        <th className="px-3 py-2">{t('launchControl.colBy')}</th>
                        <th className="px-3 py-2">{t('launchControl.colReason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h, i) => (
                        <tr key={i} className="border-b border-slate-100" data-testid={`launch-control-history-row-${i}`}>
                          <td className="px-3 py-2 text-slate-500">{h.at ? formatDateTimeLocale(h.at) : '—'}</td>
                          <td className="px-3 py-2">
                            <span className={
                              h.enabled
                                ? 'px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium'
                                : 'px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium'
                            }>
                              {h.enabled ? t('launchControl.on') : t('launchControl.off')}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-600">{h.by || '—'}</td>
                          <td className="px-3 py-2 text-slate-500">{h.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!saving) setDialogOpen(o); }}>
        <DialogContent data-testid="launch-control-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {pendingEnabled ? t('launchControl.confirmOnTitle') : t('launchControl.confirmOffTitle')}
            </DialogTitle>
            <DialogDescription>
              {pendingEnabled ? t('launchControl.confirmOnBody') : t('launchControl.confirmOffBody')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-xs text-slate-600 mb-1">{t('launchControl.reasonLabel')}</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('launchControl.reasonPlaceholder')}
              data-testid="launch-control-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}
                    data-testid="launch-control-cancel">
              {t('launchControl.cancel')}
            </Button>
            <Button onClick={confirmToggle} disabled={saving} data-testid="launch-control-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('launchControl.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
